import { useFilials, useNetworks, useFilialShapki, useSetCurrentFilial, groupFilialsByNetwork } from '../hooks';

/**
 * FilialPicker — dropdown в верхней панели AppShell.
 * При смене значения:
 *   - кладёт в Zustand store + localStorage (persist middleware)
 *   - обновляет URL ?filial_id=N
 *   - invalidate TanStack-кешей sites/forms/customers/services/popups
 *
 * Если у user'а доступен один филиал (или ни одного) — picker всё равно показывается
 * как информативный шильдик «📍 Beauty Medical» (можно вернуть «Все филиалы»).
 */
export function FilialPicker({ vShapke = false }: { vShapke?: boolean } = {}) {
  const filials = useFilials();
  const networks = useNetworks();
  const setCurrent = useSetCurrentFilial();

  const list = filials.data ?? [];
  const groups = groupFilialsByNetwork(list, networks.data);

  /**
   * ⛔ ЧЕЙ ДЕНЬ ОТКРЫТ — ГОВОРИТ АДРЕС, А НЕ ТОЛЬКО ПАМЯТЬ БРАУЗЕРА (`s311`,
   * продолжение жалобы владельца «поменял на СПб, а календарь открывает МСК»).
   *
   * Переключатель показывал значение из своего хранилища, а экран рисовал день
   * по `?filial_id` из адреса. Зайди по ссылке на чужой филиал — и вверху
   * стояло «Все филиалы», пока журнал показывал Москву: два ответа на один
   * вопрос. Адрес главнее: он и есть то, что человек видит на экране.
   */
  // Одно выражение с экранами (`useFilialShapki`, `s635` Д2): экран и шапка не расходятся.
  const effektivnyy = useFilialShapki();
  const currentFilial = effektivnyy ? list.find((f) => f.id === effektivnyy) : null;

  const onChange = (raw: string) => {
    if (raw === 'all') {
      setCurrent(null);
    } else {
      const id = Number(raw);
      if (Number.isFinite(id) && id > 0) setCurrent(id);
    }
  };

  if (filials.isLoading) {
    return <div className="px-1 text-ops-micro text-ops-sidebar-text-muted">Филиалы загружаются…</div>;
  }
  if (filials.isError) {
    return <div className="px-1 text-ops-micro text-rose-300">Филиалы не загрузились</div>;
  }

  /*
   * ⛔ ВИД ПОД ТЁМНОЕ МЕНЮ, А НЕ ПОД ПРЕЖНЮЮ ВЕРХНЮЮ ПАНЕЛЬ (`s311`, владелец
   * 09.09.2026: «сами кнопки расползлись криво пипец»). Переключатель уехал в
   * левую колонку над календарём, а вёрстка осталась от места, где он когда-то
   * висел: значок-эмодзи, узкая полупрозрачная плашка и ширина по содержимому —
   * рядом с календарём он наезжал и читался как случайный элемент. Теперь это
   * плашка во всю ширину колонки, как и всё остальное в ней.
   */
  return (
    /* ⛔ БЕЗ ПОДПИСИ «ФИЛИАЛ» (владелец, 09.09.2026): в списке уже стоит имя
       филиала, и слово над ним — то же самое дважды, ровно как «Филиал: [Лэш
       Москва]» в шапке журнала, снятое ещё `s307`. */
    /* ⛔ В ШАПКЕ ОН НЕ ТЯНЕТСЯ ВО ВСЮ ШИРИНУ (`s402`, Д-29). В левой колонке
       плашка была шириной с календарь — там это верно; в шапке такая же строка
       отобрала бы место у телефонии и кнопок справа. Ширина по содержимому,
       длинное имя филиала обрезается многоточием, а не ломает строку. */
    <label className={vShapke ? 'block min-w-0 max-w-[220px]' : 'block'}>
      <div className="relative">
        <select
          value={currentFilial ? String(currentFilial.id) : 'all'}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Филиал"
          /* ⛔ В ШАПКЕ ДРУГИЕ ЦВЕТА, И ЭТО НЕ ВКУСОВЩИНА (`s402`, Д-29).
             Плашка рисовалась под ТЁМНОЕ меню — белый текст на белой плёнке.
             Перенеси её в светлую шапку как есть — и она станет невидимой:
             ровно это и вышло на первом выкате, переключатель филиала пропал
             с экрана вовсе. Тот же урок, что `s310` записала про `option`
             в тёмной шкуре: цвет берётся от ФОНА, на котором элемент стоит. */
          className={
            (vShapke
              ? 'w-full max-w-[220px] truncate border-ops-border bg-ops-surface-2 py-2 pl-3 pr-8 text-ops-text '
              : 'w-full border-white/15 bg-white/10 px-3 py-2 pr-8 text-white ')
            + 'appearance-none rounded-ops-md border text-ops-label font-semibold outline-none focus:ring-2 focus:ring-ops-primary/40'
          }
        >
          {/* ⛔ Цвета опций литералом: токен внутри тёмной шкуры разворачивается
              в белый, и текст пунктов пропадал на белом фоне попапа (урок `s310`). */}
          <option value="all" className="bg-white font-semibold text-[#1B2540]">Все филиалы</option>
          {/* ⛔⛔ СТРОКИ УРОВНЯ СЕТИ В СПИСКЕ НЕТ (`s333`, слово владельца
              19.09.2026: «лишняя строка, просто все филиалы или выбор по
              одному»). Филиалы стояли внутри `optgroup` с именем сети — и
              «Лэш-сеть» читалась как ещё один выбор, хотя выбрать её нельзя:
              заголовок группы не кликается. Уровней ровно два — «все» или
              один филиал; третьего в этом списке не существует. */}
          {groups.flatMap((g) => g.filials).map((f) => (
            <option key={f.id} value={String(f.id)} className="bg-white font-semibold text-[#1B2540]">
              {f.name}
            </option>
          ))}
        </select>
        <span
          aria-hidden
          className={
            'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 '
            + (vShapke ? 'text-ops-text-secondary' : 'text-white/80')
          }
        >
          ▾
        </span>
      </div>
    </label>
  );
}
